import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ExternalLink, Save } from 'lucide-react';
import ReportSummary from '../components/reports/ReportSummary';
import PipelineChart from '../components/reports/charts/PipelineChart';
import RevenueChart from '../components/reports/charts/RevenueChart';
import ForecastChart from '../components/reports/charts/ForecastChart';
import MatrixTable from '../components/reports/MatrixTable';
import SaveReportModal from '../components/reports/SaveReportModal';
import { createSavedReport } from '../api';
import { useToast } from '../context/ToastContext';

// A report the assistant ran, drawn with the Reports page's own components
// from the engine's own numbers. Open it on the Reports page with the same
// configuration, or save it there under a name.

const rangeLabel = (r) => (r && r.startDate && r.endDate ? `${r.startDate} to ${r.endDate}` : '');

export default function ReportCard({ report }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { report_type: type, title, config, path, data } = report;

  const save = async ({ name, is_shared }) => {
    try {
      await createSavedReport({ name, report_type: type, config, is_shared });
      toast.success(`Saved “${name}” to Reports`);
    } catch (err) {
      toast.error('Could not save the report');
      throw err;
    }
  };

  return (
    <div className="asst-report" role="figure" aria-label={title}>
      <div className="asst-report-head">
        <BarChart3 size={14} aria-hidden="true" />
        <div className="asst-report-title">
          <strong>{title}</strong>
          <span className="asst-report-range">{rangeLabel(data.dateRange)}</span>
        </div>
      </div>
      {type !== 'matrix' && <ReportSummary reportType={type} summary={data.summary} />}
      <div className="asst-report-body">
        {type === 'sales_pipeline' && <PipelineChart data={data.data} groupBy={config.groupBy} />}
        {type === 'revenue' && <RevenueChart data={data.data} groupBy={config.groupBy} />}
        {type === 'forecast' && <ForecastChart data={data.data} groupBy={config.groupBy} />}
        {type === 'matrix' && (
          <div className="asst-report-matrix">
            <MatrixTable
              data={data.data}
              rowDimensions={data.rowDimensions}
              rowDimensionLabels={data.rowDimensionLabels}
              colLabels={data.colLabels}
              colTotals={data.colTotals}
              grandTotal={data.grandTotal}
              metricLabel={data.metricLabel}
              colDimensionLabel={data.colDimensionLabel}
            />
          </div>
        )}
        {type !== 'matrix' && (!data.data || data.data.length === 0) && <p className="asst-report-empty">Nothing in this range.</p>}
      </div>
      <div className="asst-report-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(path)}><ExternalLink size={13} aria-hidden="true" /> Open in Reports</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSaving(true)}><Save size={13} aria-hidden="true" /> Save…</button>
      </div>
      <SaveReportModal isOpen={saving} onClose={() => setSaving(false)} onSave={save} editingReport={null} />
    </div>
  );
}
